import React, { useEffect, useState } from 'react';
import { FONTS } from '../theme';
import { fetchVersion, getVersionState, recordServerSha, shortSha } from '../../hooks/useBuildVersionCheck';
import { buildLabel } from '../../../config/environment/releaseVersion';
import { useHudTomeVibe } from './useHudTomeVibe';

/**
 * LobbyInfoPanel — top-center HUD strip showing live lobby population +
 * map name + PvP mode badge. Always visible when connected to a room
 * (silent on auth/landing screens).
 *
 * Subscription pattern mirrors DrainOverlay / DeathOverlay / DuelOverlays —
 * poll-bind to the room every 500ms so it survives reconnects.
 *
 * Reads:
 *   - room.state.players (Map) → live count
 *   - room.state.mapId (string) → current map identifier (server-set)
 *   - room.state.pvpEnabled (boolean | undefined) → resolved PvP gate
 *
 * If pvpEnabled is undefined the panel falls back to NEUTRAL (gray) —
 * older server builds don't expose the resolved flag and we don't want
 * the panel to flicker red.
 *
 * Mounted from src/main.tsx alongside DeathOverlay + DuelOverlays +
 * DrainOverlay.
 *
 * data-testid: 'lobby-info-panel' for future smoke tests.
 */

interface LobbyState {
  playerCount: number;
  mapId: string;
  /** The raw map id, for the zone's tome vibe (mapId may be the display name). */
  zoneId: string;
  pvpEnabled: boolean | null;
}

const POLL_INTERVAL_MS = 500;

// Acronyms that should keep their canonical capitalization after the
// generic title-case pass below. Add new entries when more arrive.
const NAME_ACRONYMS: Record<string, string> = {
  pve: 'PvE',
  pvp: 'PvP',
  npc: 'NPC',
  hud: 'HUD',
  dj: 'DJ',
};

const formatMapName = (mapId: string): string => {
  if (!mapId || mapId === 'unknown') return 'Unknown Map';
  return mapId
    .replace(/^dj-/, '')
    .split('-')
    .map(w => NAME_ACRONYMS[w] ?? (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

export const LobbyInfoPanel: React.FC = () => {
  const [state, setState] = useState<LobbyState | null>(null);

  // Build chip (DJ ask 2026-07-10): the deployed sha in the always-visible
  // map pill, for eyeballing stale-cache suspicions without opening the
  // picker or /diagnostic. Same source as the picker chip: the shared
  // version store, else a one-shot /api/version fetch. `drift` flips true
  // when the server redeployed under this open page — the strongest "you
  // are running an old bundle" signal we have client-side.
  const [build, setBuild] = useState<{ sha: string; drift: boolean } | null>(() => {
    const s = getVersionState();
    return s.hasData && s.lastServerSha ? { sha: s.lastServerSha, drift: s.drift } : null;
  });
  useEffect(() => {
    if (build) return;
    let cancelled = false;
    void fetchVersion().then((v) => {
      if (cancelled || !v) return;
      recordServerSha(v.sha, v.env);
      const s = getVersionState();
      setBuild({ sha: v.sha, drift: s.drift });
    });
    return () => { cancelled = true; };
  }, [build]);

  useEffect(() => {
    let lastBoundRoom: any = null;

    const tick = () => {
      const room = (window as any).nm?.getRoom?.();
      if (!room || !room.state) {
        setState(null);
        lastBoundRoom = null;
        return;
      }
      // Re-bind tracking — no per-room subscription yet (state is read-
      // through; Colyseus schema syncs the players Map continuously).
      if (room !== lastBoundRoom) lastBoundRoom = room;

      const players = room.state.players;
      const playerCount =
        players && typeof players.size === 'number'
          ? players.size
          : players && typeof players.forEach === 'function'
            ? (() => { let n = 0; players.forEach(() => n++); return n; })()
            : 0;

      // Prefer the display-friendly mapName (e.g. "PvE Arena") set by
      // GameRoom from level.name. Fall back to mapId (e.g. "pve-arena")
      // formatted via formatMapName, then to legacy fallbacks for
      // older server builds.
      const mapName: string | undefined = room.state.mapName || undefined;
      const mapId: string =
        room.state.mapId
        ?? room.state.currentMapId
        ?? room.state.metadata?.mapId
        ?? '';

      const pvpEnabled: boolean | null =
        typeof room.state.pvpEnabled === 'boolean'
          ? room.state.pvpEnabled
          : typeof room.state.metadata?.pvp?.enabled === 'boolean'
            ? room.state.metadata.pvp.enabled
            : null;

      const next = { playerCount, mapId: mapName ?? mapId, zoneId: mapId, pvpEnabled };
      // Same fields → keep the old object (B7: a new object every poll
      // re-rendered the panel twice a second with nothing changed).
      setState(prev => (prev && prev.playerCount === next.playerCount && prev.mapId === next.mapId
        && prev.zoneId === next.zoneId && prev.pvpEnabled === next.pvpEnabled ? prev : next));
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const tome = useHudTomeVibe(state?.zoneId);
  if (!state) return null;

  // Stream note 17 (2026-10-08): the pill wears the zone's tome vibe (plan 17
  // tokens: dark HUD ground, gilt rule, parchment ink) and shows the release
  // tag baked at deploy, not the commit; untagged builds keep the short sha.
  const pvp = state.pvpEnabled === true
    ? { text: 'PvP on', tone: 'var(--tome-danger)', title: 'Players can fight each other on this map' }
    : state.pvpEnabled === false
      ? { text: 'PvP off', tone: 'var(--tome-moss-soft)', title: 'Players cannot fight each other on this map' }
      : { text: 'PvP ?', tone: 'var(--tome-ink-muted)', title: 'PvP state unknown (older server)' };
  const label = build ? buildLabel(shortSha(build.sha)) : undefined;
  const dot = <span aria-hidden="true" style={{ color: 'var(--tome-hud-rule)', fontSize: 7 }}>◆</span>;

  return (
    <div
      data-testid="lobby-info-panel"
      className={tome.className}
      style={{
        ...tome.style,
        // Sits BELOW the ClassSwitcherTopBar (top: 8, ~50px tall); 64px clears
        // it with an 8px gap (DJ flagged the overlap, 2026-05-01).
        position: 'fixed',
        top: 64,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '4px 13px 4px 12px',
        background: 'linear-gradient(180deg, var(--tome-hud-ground-hi), var(--tome-hud-ground))',
        border: '1px solid var(--tome-hud-rule)',
        borderRadius: 'var(--tome-chip-cut-1)',
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.35), 0 3px 8px rgba(0, 0, 0, 0.35)',
        color: 'var(--tome-hud-ink)',
        fontSize: 12,
        fontFamily: FONTS.ui,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 50,
        userSelect: 'none',
      }}
    >
      <span style={{ fontFamily: FONTS.display, fontWeight: 700, letterSpacing: '0.05em', color: 'var(--tome-gilt-bright)' }}>
        {formatMapName(state.mapId)}
      </span>
      {dot}
      <span title="Players in this room" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span aria-hidden="true" style={{ opacity: 0.75, fontSize: 11 }}>👥</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{state.playerCount}</span>
      </span>
      {dot}
      <span
        data-testid="lobby-info-pvp"
        title={pvp.title}
        style={{
          padding: '1px 7px',
          borderRadius: 'var(--tome-chip-cut-2)',
          border: `1px solid color-mix(in srgb, ${pvp.tone} 80%, transparent)`,
          background: `color-mix(in srgb, ${pvp.tone} 34%, transparent)`,
          color: 'var(--tome-hud-ink)',
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: '0.05em',
        }}
      >
        {pvp.text}
      </span>
      {label && (
        <>
          {dot}
          <span
            data-testid="lobby-info-build"
            style={{
              fontSize: 10,
              letterSpacing: '0.04em',
              fontVariantNumeric: 'tabular-nums',
              color: build!.drift ? 'var(--tome-danger)' : 'var(--tome-hud-ink-soft)',
              fontWeight: build!.drift ? 700 : 500,
            }}
            title={build!.drift
              ? 'Server redeployed under this page — you are running an OLD bundle; reload'
              : `Build ${shortSha(build!.sha)}`}
          >
            {build!.drift ? '⚠ ' : ''}{label}
          </span>
        </>
      )}
    </div>
  );
};
